export type BoxSource = 'auto' | 'notes2' | 'notes3' | 'notes4' | 'notes5' | 'notes6' | 'notes7' | 'notes8' | 'manual';

export interface LeadingDocumentLike {
    id?: string | number | null;
    type?: string | null;
    status?: string | null;
    issueDate?: unknown;
    date?: unknown;
    nkObject?: any;
    header?: any;
}

export interface AutomaticBoxResolution {
    value: number | null;
    source: 'document_auto' | 'order_auto' | 'unavailable';
    warning?: string;
}

export interface DriverRouteDraftRow {
    checked: boolean;
    orderId: string | number;
    date: string;
    boxes: number | null;
    boxSource: BoxSource;
    sourceOverride: boolean;
    editableFields?: string[];
    editableFieldLabels?: Record<string, string>;
}

export interface DriverRouteStop {
    order_id: string;
    date: string;
    position: number;
    boxes: number;
    boxes_source: BoxSource;
    boxes_override: boolean;
    editable_fields: string[];
    field_labels: Record<string, string>;
}

export interface DriverLinkRequestPayload {
    mode: 'orders';
    perm: number;
    orderIds: string[];
    routeStops: DriverRouteStop[];
}

const LEADING_DOCUMENT_TYPES = new Set(['invoice', 'delivery_certificate']);

function asTimestamp(value: unknown): number {
    if (value instanceof Date) {
        const time = value.getTime();
        return Number.isFinite(time) ? time : 0;
    }
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value !== 'string' || !value.trim()) return 0;
    const parsed = Date.parse(value.trim().replace(' ', 'T'));
    return Number.isFinite(parsed) ? parsed : 0;
}

export function leadingDocumentTimestamp(document: LeadingDocumentLike): number {
    const candidates = [
        document.issueDate,
        document.nkObject?.action_time,
        document.header?.dateIssued,
        document.header?.date,
        document.nkObject?.date_issued,
        document.date,
    ];
    for (const candidate of candidates) {
        const timestamp = asTimestamp(candidate);
        if (timestamp) return timestamp;
    }
    return 0;
}

/** Newest active invoice/delivery certificate, ordered by timestamp and then document id. */
export function selectLatestLeadingDocument<T extends LeadingDocumentLike>(documents?: T[] | null): T | undefined {
    return (documents ?? [])
        .filter((document) =>
            LEADING_DOCUMENT_TYPES.has(String(document?.type ?? '')) &&
            String(document?.status ?? '').toLowerCase() !== 'canceled',
        )
        .reduce<T | undefined>((latest, candidate) => {
            if (!latest) return candidate;
            const candidateTime = leadingDocumentTimestamp(candidate);
            const latestTime = leadingDocumentTimestamp(latest);
            if (candidateTime !== latestTime) return candidateTime > latestTime ? candidate : latest;
            const candidateId = Number(candidate.id);
            const latestId = Number(latest.id);
            if (Number.isFinite(candidateId) && Number.isFinite(latestId)) {
                return candidateId > latestId ? candidate : latest;
            }
            return String(candidate.id ?? '').localeCompare(String(latest.id ?? '')) > 0 ? candidate : latest;
        }, undefined);
}

export function dateKey(value: unknown): string | null {
    if (value instanceof Date && Number.isFinite(value.getTime())) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (iso) {
        const normalized = `${iso[1]}-${iso[2]}-${iso[3]}`;
        const parsed = new Date(`${normalized}T12:00:00`);
        return Number.isFinite(parsed.getTime()) &&
            parsed.getFullYear() === Number(iso[1]) &&
            parsed.getMonth() + 1 === Number(iso[2]) &&
            parsed.getDate() === Number(iso[3])
            ? normalized
            : null;
    }
    const local = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(trimmed);
    if (!local) return null;
    const normalized = `${local[3]}-${String(Number(local[2])).padStart(2, '0')}-${String(Number(local[1])).padStart(2, '0')}`;
    return dateKey(normalized);
}

export function extractDeliveryDate(row: Record<string, any>): string | null {
    return dateKey(row?.delivery_date) ?? dateKey(row?.date);
}

/** Groups without changing row order or the first-seen order of date groups. */
export function groupRowsByDeliveryDate<T>(
    rows: T[],
    getDate: (row: T) => string | null = (row) => extractDeliveryDate(row as any),
): Array<{ date: string | null; rows: T[] }> {
    const groups = new Map<string, {date: string | null; rows: T[]}>();
    rows.forEach((row) => {
        const date = getDate(row);
        const key = date ?? '__missing__';
        const group = groups.get(key) ?? {date, rows: []};
        group.rows.push(row);
        groups.set(key, group);
    });
    return Array.from(groups.values());
}

/** Strict non-negative numeric parsing for notes used as box counts. */
export function parseNumericNote(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
    if (typeof value !== 'string') return null;
    const normalized = value.trim().replace(',', '.');
    if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** A linked document leads; the order estimate is only a warned fallback. */
export function resolveAutomaticBoxes(
    linkedDocumentEstimate: unknown,
    orderEstimate: unknown,
): AutomaticBoxResolution {
    const documentValue = parseNumericNote(linkedDocumentEstimate);
    if (documentValue != null) return {value: documentValue, source: 'document_auto'};
    const orderValue = parseNumericNote(orderEstimate);
    if (orderValue != null) {
        return {
            value: orderValue,
            source: 'order_auto',
            warning: 'לא ניתן לחשב קרטונים מהמסמך המקושר; נעשה שימוש בהזמנה.',
        };
    }
    return {
        value: null,
        source: 'unavailable',
        warning: 'לא נמצא ערך קרטונים תקין. יש להזין תיקון ידני.',
    };
}

/** Builds compact per-date route positions from checked rows only. */
export function createDriverLinkRequestPayload(
    rows: DriverRouteDraftRow[],
    perm: number,
): DriverLinkRequestPayload {
    const positionsByDate = new Map<string, number>();
    const routeStops: DriverRouteStop[] = [];
    rows.forEach((row) => {
        if (!row.checked || !dateKey(row.date) || parseNumericNote(row.boxes) == null) return;
        const date = dateKey(row.date)!;
        const position = (positionsByDate.get(date) ?? 0) + 1;
        positionsByDate.set(date, position);
        routeStops.push({
            order_id: String(row.orderId),
            date,
            position,
            boxes: Number(row.boxes),
            boxes_source: row.boxSource,
            // Any note/manual source is an explicit office override. Automatic
            // values are recomputed by the backend from the current leading document.
            boxes_override: row.boxSource !== 'auto' || row.sourceOverride,
            editable_fields: (row.editableFields ?? []).filter((field) =>
                ['notes2', 'notes3', 'notes4', 'notes5', 'notes6', 'notes7', 'notes8'].includes(field)),
            field_labels: Object.fromEntries(
                Object.entries(row.editableFieldLabels ?? {}).filter(([field]) =>
                    ['notes2', 'notes3', 'notes4', 'notes5', 'notes6', 'notes7', 'notes8'].includes(field)),
            ),
        });
    });
    return {
        mode: 'orders',
        perm,
        orderIds: routeStops.map((stop) => stop.order_id),
        routeStops,
    };
}

export function formatIsraelExpiry(date: string): string {
    const normalized = dateKey(date);
    if (!normalized) return '';
    const [year, month, day] = normalized.split('-');
    return `${day}/${month}/${year} 23:59:59 (שעון ישראל)`;
}
